import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { SCHEDULE, PDF_BG } from "../data/content";
import SectionBg from "./SectionBg";

const TYPE_ICONS = {
    "Arrival & Networking": Icons.Users,
    "Opening Ceremony": Icons.Sparkles,
    "Keynote Session": Icons.Mic,
    "Panel Discussion": Icons.MessageCircle,
    Networking: Icons.Users,
    Workshop: Icons.Cpu,
    "Networking Break": Icons.Coffee,
    "Fireside Chat": Icons.Flame,
    Recognition: Icons.Star,
    Entertainment: Icons.Music,
    Masterclass: Icons.Crown,
    Showcase: Icons.Monitor,
    "Closing Ceremony": Icons.Award,
};

function TypeIcon({ type }) {
    const Icon = TYPE_ICONS[type] || Icons.CircleDot;
    return <Icon size={22} strokeWidth={1.7} aria-hidden />;
}

export default function Schedule() {
    return (
        <section
            id="schedule"
            className="relative py-24 md:py-36 overflow-hidden"
            data-testid="schedule-section"
        >
            <SectionBg src={PDF_BG.schedule} opacity={0.42} />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 mb-12 md:mb-14">
                    <div className="col-span-12 md:col-span-4 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono">
                        The Programme
                    </div>
                    <h2 className="col-span-12 md:col-span-8 min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl">
                        Two days, <span className="gradient-text">non-stop</span>.
                    </h2>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-10 xl:gap-12">
                    {SCHEDULE.map((day, di) => (
                        <motion.div
                            key={day.day}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ delay: di * 0.15, duration: 0.8 }}
                            className="relative"
                            data-testid={`schedule-day-${di}`}
                        >
                            <div className="mb-7 flex flex-wrap items-end justify-between gap-4 border-b border-white/10 pb-5">
                                <div>
                                    <div className="mb-2 flex min-w-0 items-center gap-2 text-xs uppercase tracking-[0.18em] sm:tracking-[0.24em] text-[#FF9A5D] font-mono">
                                        <Icons.Calendar size={14} /> {day.date}
                                    </div>
                                    <div className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-normal sm:tracking-tighter">
                                        {day.day}
                                    </div>
                                </div>
                            </div>

                            <div className="relative">
                                {day.sessions.map((s, si) => (
                                    <motion.div
                                        key={s.title}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.2 + si * 0.04 }}
                                        className="group grid min-h-[58px] grid-cols-[76px_22px_minmax(0,1fr)] items-start gap-x-2 py-3 sm:grid-cols-[140px_28px_minmax(0,1fr)] sm:gap-x-3 md:grid-cols-[178px_34px_minmax(0,1fr)_155px_32px] md:gap-x-4 2xl:grid-cols-[188px_34px_minmax(0,1fr)_170px_32px]"
                                        data-testid={`schedule-session-${di}-${si}`}
                                    >
                                        <div className="pt-1 font-mono text-[10px] leading-tight text-[#FF8A45] sm:whitespace-nowrap sm:text-xs md:text-[13px]">
                                            {s.time}
                                        </div>
                                        <div className="relative flex h-full justify-center">
                                            <span className="absolute bottom-[-10px] top-[-10px] w-px bg-[#E4A06D]/55" />
                                            <span className="relative mt-1 h-4 w-4 rounded-full border-2 border-[#E4A06D] bg-[#0A0A0A] shadow-[0_0_0_4px_rgba(255,107,53,0.08)]" />
                                        </div>
                                        <div className="min-w-0 pt-0.5 text-sm font-semibold leading-snug text-zinc-100 transition group-hover:text-white md:text-base [overflow-wrap:anywhere]">
                                            {s.title}
                                        </div>
                                        <div className="col-start-3 mt-2 min-w-0 border-l border-[#B88A67]/45 pl-3 text-xs leading-snug text-zinc-300 sm:pl-4 md:col-start-auto md:mt-0 md:pt-1 md:text-[13px] 2xl:text-sm">
                                            {s.type}
                                        </div>
                                        <div className="hidden pt-0.5 text-[#D99667] md:flex md:justify-end">
                                            <TypeIcon type={s.type} />
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
